import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBar, BottomNav } from "../components/Shell";
import { useAppStore } from "../components/AppStore";
import SosDialer from "../components/SosDialer";
import { currentUser } from "../mock";
import { ChevronLeft, PhoneCall, MapPin, ShieldCheck, UserCheck, Navigation, X, CheckCircle2, Pencil } from "lucide-react";

export default function Safety() {
  const navigate = useNavigate();
  const { emergency } = useAppStore();
  const [alerting, setAlerting] = useState(false);
  const [sent, setSent] = useState(false);
  const [dialer, setDialer] = useState({ open: false, edit: false });
  const telHref = `tel:${(emergency.phone || "").replace(/[^0-9+]/g, "")}`;

  const trigger = () => {
    setAlerting(true);
    setTimeout(() => { setAlerting(false); setSent(true); }, 1600);
  };

  return (
    <div className="gd-screen" style={{ background: "linear-gradient(175deg,#101736 0%,#1c2865 55%,#233079 100%)" }}>
      <StatusBar dark={false} />
      <div className="gd-scroll px-5 pb-4">
        <div className="flex items-center gap-2 pt-1">
          <button onClick={() => navigate(-1)} className="gd-tap grid h-10 w-10 place-items-center rounded-full bg-white/10"><ChevronLeft size={20} color="#fff" /></button>
          <h1 className="text-2xl font-extrabold text-white">Safety Center</h1>
        </div>

        {/* SOS button */}
        <div className="mt-6 flex flex-col items-center">
          <p className="text-sm font-semibold text-white/70">Press &amp; hold in an emergency</p>
          <button onClick={trigger} className="gd-tap gd-sos-pulse mt-5 grid h-48 w-48 place-items-center rounded-full text-white"
            style={{ background: "radial-gradient(circle at 35% 30%, #ff5b4a, #c0231a)", boxShadow: "0 20px 50px rgba(192,35,26,.5)", border: "6px solid rgba(255,255,255,.15)" }}>
            <div className="flex flex-col items-center">
              <PhoneCall size={44} />
              <span className="mt-2 text-3xl font-black tracking-widest">SOS</span>
              <span className="text-[11px] font-semibold text-white/80">One-touch call</span>
            </div>
          </button>
          <div className="mt-5 flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-bold text-white/80">
            <UserCheck size={15} className="text-[#f5911e]" /> Alerts {emergency.name} instantly
          </div>
          <button onClick={() => setDialer({ open: true, edit: true })} className="gd-tap mt-2 flex items-center gap-1.5 text-xs font-bold text-white/70">
            <Pencil size={13} /> Program emergency number
          </button>
        </div>

        {/* live tracking map */}
        <div className="mt-6 overflow-hidden rounded-[22px] bg-white p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-extrabold text-[#1f2a52]">Real-Time Tracking</h3>
            <span className="flex items-center gap-1 rounded-full bg-[#eaf6ea] px-2 py-1 text-[10px] font-bold text-[#3d8f34]"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#4fae3f]" /> LIVE</span>
          </div>
          <div className="relative mt-3 h-40 overflow-hidden rounded-2xl" style={{ background: "linear-gradient(135deg,#e8edff,#dbe4ff)" }}>
            <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(#c9d4ff 1px,transparent 1px),linear-gradient(90deg,#c9d4ff 1px,transparent 1px)", backgroundSize: "26px 26px" }} />
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 300 160">
              <path d="M20 130 Q90 90 150 100 T280 40" fill="none" stroke="#3b57c9" strokeWidth="4" strokeLinecap="round" strokeDasharray="2 10" opacity="0.6" />
            </svg>
            <div className="absolute" style={{ left: 46, top: 116 }}>
              <div className="grid h-7 w-7 place-items-center rounded-full bg-[#4fae3f] text-white shadow-lg"><Navigation size={14} /></div>
              <span className="mt-1 block rounded-full bg-white px-2 py-0.5 text-[9px] font-bold text-[#3d8f34] shadow">Home</span>
            </div>
            <div className="absolute" style={{ right: 22, top: 24 }}>
              <div className="gd-float grid h-8 w-8 place-items-center rounded-full bg-[#f5911e] text-white shadow-lg"><MapPin size={16} /></div>
              <span className="mt-1 block rounded-full bg-white px-2 py-0.5 text-[9px] font-bold text-[#e97f0c] shadow">Jordan</span>
            </div>
          </div>
          <p className="mt-2 text-[11px] font-semibold text-[#9aa3c0]">Live location shared with parent while a job is active — 0.3 mi from home.</p>
        </div>

        {/* emergency contacts */}
        <div className="mt-4 rounded-[22px] bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-extrabold text-[#1f2a52]">Emergency Contacts</h3>
            <button onClick={() => setDialer({ open: true, edit: true })} className="gd-tap flex items-center gap-1 text-xs font-bold text-[#3b57c9]"><Pencil size={12} /> Edit</button>
          </div>
          <Contact initial={(emergency.name || "?").charAt(0).toUpperCase()} name={emergency.name} rel={emergency.phone} color="#3b57c9" href={telHref} />
          <Contact initial="9" name="Local Emergency (911)" rel="One-touch dial" color="#e0533d" href="tel:911" />
        </div>

        {/* safety promise */}
        <div className="mt-4 rounded-[22px] bg-white/10 p-4 backdrop-blur">
          <div className="flex items-center gap-2 text-white"><ShieldCheck size={18} className="text-[#4fae3f]" /><span className="text-sm font-extrabold">Our Safety Promise</span></div>
          <p className="mt-2 text-[12px] leading-relaxed text-white/70">Every poster is background-checked, every job is parent-approved, and tracking + SOS stay on for the whole task. Safety is our #1 priority.</p>
        </div>
      </div>

      <BottomNav />

      {alerting && (
        <div className="absolute inset-0 z-50 grid place-items-center bg-[#c0231a]/95">
          <div className="flex flex-col items-center text-white">
            <div className="gd-sos-pulse grid h-24 w-24 place-items-center rounded-full bg-white/20"><PhoneCall size={40} /></div>
            <p className="mt-5 text-lg font-black">Alerting {emergency.name}…</p>
            <p className="text-sm text-white/80">Sharing your live location</p>
          </div>
        </div>
      )}
      {sent && (
        <div className="absolute inset-0 z-50 flex items-end bg-black/40">
          <div className="gd-enter relative w-full rounded-t-[28px] bg-white p-6 pb-8 text-center">
            <button onClick={() => setSent(false)} className="absolute right-5 top-5 text-[#a3abc9]"><X size={20} /></button>
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[#e6e9f5]" />
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#eaf6ea]"><CheckCircle2 size={34} className="text-[#4fae3f]" /></div>
            <h3 className="mt-3 text-xl font-black text-[#1f2a52]">Alert Sent (demo)</h3>
            <p className="mt-1 text-sm text-[#5b648a]">{emergency.name} has been notified with your live location. Help is on the way.</p>
            <a href={telHref} className="gd-tap mt-5 flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 font-extrabold text-white" style={{ background: "linear-gradient(180deg,#ff5b4a,#c0231a)" }}>
              <PhoneCall size={18} /> Call {emergency.name} Now
            </a>
            <button onClick={() => setSent(false)} className="mt-2 w-full rounded-2xl py-3 font-bold text-[#5b648a]">Close</button>
          </div>
        </div>
      )}
      <SosDialer open={dialer.open} startEditing={dialer.edit} onClose={() => setDialer({ open: false, edit: false })} />
    </div>
  );
}

function Contact({ initial, name, rel, color, href }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-[#f7f8fd] p-3">
      <div className="grid h-11 w-11 place-items-center rounded-full font-extrabold text-white" style={{ background: color }}>{initial}</div>
      <div className="flex-1"><p className="text-[13px] font-bold text-[#1f2a52]">{name}</p><p className="text-[11px] font-semibold text-[#9aa3c0]">{rel}</p></div>
      <a href={href || "tel:911"} className="gd-tap grid h-9 w-9 place-items-center rounded-full bg-[#eaf6ea]"><PhoneCall size={16} className="text-[#4fae3f]" /></a>
    </div>
  );
}
